import type { JSX } from "react";
import type { Citation } from "@shared/types";
import { IconFile, IconGlobe } from "../icons";

interface CitationChipProps {
  citation: Citation;
  active: boolean;
  onClick: (citation: Citation) => void;
}

/**
 * Human-readable source-type label for the trust UI (§2-3): which kind of
 * source backs this citation. Shared by the chip tooltip and the right rail so
 * the wording stays consistent. A low-confidence near-miss is labeled as such
 * rather than by its origin, since it is not a verified answer source.
 */
export function sourceTypeLabel(c: Citation): string {
  if (c.lowConfidence === true) return "低置信，仅供参考";
  if (c.origin === "personal") return "你的资料";
  if (c.origin === "feishu-user") return "你的飞书";
  if (c.origin === "artifact") return "小环生成";
  if (c.origin === "feishu") return "飞书 wiki";
  if (c.origin === "web" || (c.url !== undefined && c.url.length > 0)) return "公开网络";
  if (c.origin === "local") return "本地文档";
  return "内部知识库";
}

/**
 * First-class citation chip (cycledesign §1.11.2): clickable, source-typed,
 * and visually distinct between a verified internal KB source and a retrieved
 * web source. Hover reveals a snippet preview so the user can vet the source
 * without leaving the answer.
 */
export function CitationChip({ citation, active, onClick }: CitationChipProps): JSX.Element {
  const isWeb = citation.url !== undefined && citation.url.length > 0;
  // A low-confidence KB near-miss is surfaced for inspection, not as an answer
  // source (§1.11.1). It must read differently — "可能相关" rather than a
  // numbered "来源N" — so the user never mistakes it for grounding.
  const low = citation.lowConfidence === true;
  const className = `cite-chip ${isWeb ? "web" : "kb"} ${low ? "low" : ""} ${active ? "active" : ""}`;
  const inner = (
    <>
      <span className="cite-icon" aria-hidden="true">
        {isWeb ? <IconGlobe size={11} /> : <IconFile size={11} />}
      </span>
      <span className="cite-label">{citation.title || citation.source}</span>
      <span className="cite-kind">{low ? "可能相关" : isWeb ? "网页" : `来源${citation.num}`}</span>
      <span className="cite-tooltip" role="tooltip">
        <span className="cite-tooltip-head">
          {sourceTypeLabel(citation)} · {citation.source}
        </span>
        {citation.snippet !== undefined && citation.snippet.length > 0 && (
          <span className="cite-tooltip-snippet">{citation.snippet}</span>
        )}
        {isWeb && <span className="cite-tooltip-url">{citation.url}</span>}
      </span>
    </>
  );
  // A public web source is a real link — clicking opens it in a new tab.
  // Internal sources (when shown at all) open the inspection rail instead.
  if (isWeb) {
    return (
      <a className={className} href={citation.url} target="_blank" rel="noreferrer" onClick={() => onClick(citation)}>
        {inner}
      </a>
    );
  }
  return (
    <span
      className={className}
      role="button"
      tabIndex={0}
      onClick={() => onClick(citation)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick(citation);
      }}
    >
      {inner}
    </span>
  );
}
